import { defineComponent as _defineComponent } from 'vue'
import { mergeProps as _mergeProps, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, ref, watch, nextTick } from 'vue';
    import { VAutocomplete, VInfiniteScroll } from 'vuetify/components';
    import { debounce } from 'lodash';

    import AQChip from '@/components/shared/interactions/AQChip.vue';
    import { LoadMoreOptions } from '@/interfaces/common';

    type CommonProps = {
        items?: object[];
        fieldProps?: object;
        label?: string;
        color?: string;
        chips?: boolean;
        clearable?: boolean;
        multiple?: boolean;
        modelValue?: unknown;
        itemTitle?: string | ((item: object) => string);
        itemValue?: string | ((item: object) => string | number);
        loading?: boolean;
        itemDisabled?: boolean;
        enableInfinite?: boolean;
        returnObject?: boolean;
        required?: boolean;
        density?: 'default' | 'compact' | 'comfortable';
        colorByMemberId?: boolean;
    };

    
export default /*@__PURE__*/_defineComponent({
  __name: 'AQAutocomplete',
  props: {
    items: { default: () => [] },
    fieldProps: { default: () => ({}) },
    label: {},
    color: { default: undefined },
    chips: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    modelValue: { default: undefined },
    itemTitle: { type: [String, Function], default: 'name' },
    itemValue: { type: [String, Function], default: 'id' },
    loading: { type: Boolean },
    itemDisabled: { type: Boolean, default: false },
    enableInfinite: { type: Boolean, default: false },
    returnObject: { type: Boolean, default: false },
    required: { type: Boolean },
    density: { default: 'default' },
    colorByMemberId: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "change", "load-more", "update:search"],
  setup(__props: any, { emit: __emit }) {

    const props = __props;

    const emit = __emit;

    const innerValue = computed({
        get: () => props.modelValue,
        set: (val: string | string[]) => emit('update:modelValue', val),
    });

    const displayValue = computed({
        get: () => {
            if (!props.items.length) {
                return props.multiple ? [] : null;
            }
            return props.modelValue;
        },
        set: (val: string | string[]) => emit('update:modelValue', val),
    });

    const computedLabel = computed(() => {
        if (props.required) {
            return `${props.label} *`;
        }
        return props.label;
    });

    const searchText = ref('');
    const debouncedEmitLoadMore = debounce((options: LoadMoreOptions) => {
        if (searchText.value) {
            options.done && options.done('empty');
            return;
        }

        if (!props.items.length) {
            options.done && options.done('empty');
            return;
        }

        emit('load-more', options);
    }, 500);

    const getItemTitle = (item: object): string => {
        if (typeof props.itemTitle === 'function') {
            return props.itemTitle(item);
        }
        return (item as Record<string, unknown>)[props.itemTitle as string] as string;
    };

    watch(innerValue, (newValue: unknown) => {
        emit('change', newValue);
    });

    const debouncedEmitSearch = debounce((value: string) => {
        emit('update:search', value);
    }, 500);

    watch(searchText, (newSearch: string) => {
        // Check if search matches any item title (indicates selection, not typing)
        const isSelection = props.items.some((item) => getItemTitle(item) === newSearch);

        if (!isSelection) {
            debouncedEmitSearch(newSearch);
        }
    });

    const getMemberChipClass = (item: { raw?: unknown }): string => {
        return (item.raw as Record<string, unknown>)?.memberId ? 'recipient-chip--member' : '';
    };

    const onFocus = (e: FocusEvent) => {
        if (!(e.target instanceof HTMLInputElement)) return;
        const input = e.target;
        nextTick(() => input.select());
    };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(VAutocomplete), _mergeProps({
    modelValue: displayValue.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((displayValue).value = $event))
  }, props.fieldProps, {
    label: computedLabel.value,
    items: props.items,
    multiple: props.multiple,
    chips: props.chips,
    "closable-chips": props.chips && props.multiple,
    clearable: props.clearable,
    "item-title": props.itemTitle,
    "item-value": props.itemValue,
    loading: props.loading,
    required: props.required,
    "item-disabled": props.itemDisabled,
    "return-object": props.returnObject,
    density: __props.density,
    search: searchText.value,
    "onUpdate:search": _cache[1] || (_cache[1] = ($event: any) => ((searchText).value = $event)),
    variant: "outlined",
    rounded: "",
    autocomplete: "off",
    onFocus: onFocus
  }), _createSlots({ _: 2 }, [
    (props.colorByMemberId && props.chips && props.multiple)
      ? {
          name: "chip",
          fn: _withCtx(({ item, props: chipProps }) => [
            _createVNode(AQChip, _mergeProps(chipProps, {
              size: "small",
              variant: "flat",
              closable: "",
              class: getMemberChipClass(item),
              text: item.title
            }), null, 16, ["class", "text"])
          ]),
          key: "0"
        }
      : undefined,
    (props.enableInfinite && props.items.length && !searchText.value)
      ? {
          name: "append-item",
          fn: _withCtx(() => [
            _createVNode(_unref(VInfiniteScroll), {
              mode: "intersect",
              onLoad: _unref(debouncedEmitLoadMore)
            }, {
              empty: _withCtx(() => [
                _createElementVNode("span", null, _toDisplayString(_ctx.$t('pagination.noMoreData')), 1)
              ]),
              _: 1
            }, 8, ["onLoad"])
          ]),
          key: "1"
        }
      : undefined
  ]), 1040, ["modelValue", "label", "items", "multiple", "chips", "closable-chips", "clearable", "item-title", "item-value", "loading", "required", "item-disabled", "return-object", "density", "search"]))
}
}

})